/* ====================================================================
   HOW IT WORKS — cinematic scroll scene (pilot)
   ====================================================================
   A tall, pinned section. As the user scrolls through it, four "acts"
   advance: Trigger → Engage → Convert → Attribute.

   Stage is a theater: curtain edges, screen glow, film grain, projector
   cone. A phone on the right of the stage morphs through the four states.
   Left side holds the act copy that cross-fades with the phone.
   ==================================================================== */

const { useEffect, useRef, useState, useMemo } = React;

const ACTS = [
  {
    n: '01',
    eyebrow: 'THE INVITE',
    title: 'The lights come up.',
    body: "Guests receive a friendly text upon exiting the theater.",
    phone: 'sms',
  },
  {
    n: '02',
    eyebrow: 'THE REPLAY',
    title: "The trailers they just watched \u2014 in their hand.",
    body: "Opens in under a second. No app to download. Tonight\u2019s trailers, the ones they missed, and what\u2019s coming next \u2014 all ready to scroll.",
    phone: 'carousel',
  },
  {
    n: '03',
    eyebrow: 'THE BOOKING',
    title: "Ready to transact.",
    body: "Guests can instantly buy tickets if available, share trailers with friends, set reminders and save release dates to their calendar.",
    phone: 'ticket',
  },
  {
    n: '04',
    eyebrow: 'THE PROOF',
    title: "Valuable insights at your fingertips.",
    body: "Which theater, which showtime, which trailer. See exactly what\u2019s working. Your numbers stay yours.",
    phone: 'attribution',
  },
];

const POSTERS = [
  './marketing/posters/project-hail-mary.webp',
  './marketing/posters/scream-7.jpg',
  './marketing/posters/bone-temple.jpg',
  './marketing/posters/wuthering-heights.jpg',
  './marketing/posters/mercy.jpg',
  './marketing/posters/shelter.jpg',
];

function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
function smoothstep(t) { return t * t * (3 - 2 * t); }

function useScenePhase(sectionRef) {
  // Returns a continuous 0..ACTS.length progress across the pinned section,
  // AND writes --mmx-scroll-p (0..1) to the section for the ambient purple
  // light fade-in from the top.
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight;
        const total = el.offsetHeight - vh; // pinned scroll distance
        const scrolled = clamp(-rect.top, 0, total);
        const t = total > 0 ? scrolled / total : 0;
        setProgress(t * ACTS.length);
        // Ambient scroll-progress for the purple light that fades in as the
        // section enters the viewport. Ramps 0..1 while the section's top
        // travels from viewport bottom (rect.top = vh) to viewport top (0).
        const enter = clamp(1 - rect.top / vh, 0, 1);
        el.style.setProperty('--mmx-scroll-p', enter.toFixed(3));
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [sectionRef]);

  return progress;
}

/* ---- Phone device frame ---------------------------------------------- */

function PhoneFrame({ children, circuit = false, naked = false }) {
  return (
    <div className={`mmx-phone${circuit ? ' is-circuit' : ''}${naked ? ' is-naked' : ''}`}>
      <div className="mmx-phone-bezel">
        <div className="mmx-phone-notch" />
        <div className="mmx-phone-screen">{children}</div>
      </div>
      <div className="mmx-phone-glow" aria-hidden />
    </div>
  );
}

/* ---- Phone screen states --------------------------------------------- */

function SmsScreen() {
  return (
    <div className="mmx-state mmx-state-sms">
      <div className="mmx-sms-status">
        <span>11:05</span>
        <span className="mmx-sms-signals">•••• 5G</span>
      </div>
      <div className="mmx-sms-contact">
        <div className="mmx-sms-avatar">C</div>
        <div>
          <div className="mmx-sms-name">Cinemark</div>
          <div className="mmx-sms-handle">Text Message</div>
        </div>
      </div>
      <div className="mmx-sms-bubbles">
        <div className="mmx-sms-bubble mmx-sms-in">
          Ron, movie's over, but the fun's not. Tap <a className="mmx-sms-link" href="#">HERE</a> to replay the trailers you just saw, discover a few you didn't catch, grab exclusive deals, and be entered to win a trip for two to a real Hollywood studio.
        </div>
        <div className="mmx-sms-time">now</div>
      </div>
      <div className="mmx-sms-dots"><span /><span /><span /></div>
    </div>
  );
}

function CarouselScreen() {
  // Real engagement screenshot — the actual Cinemark-branded web app
  // moviegoers land on after tapping the SMS link. Fills the phone frame
  // edge-to-edge; no synthetic UI around it.
  return (
    <div className="mmx-state mmx-state-engage">
      <img
        className="mmx-engage-shot"
        src="./marketing/mm-screenshot2.jpeg"
        alt="Movie Minder engagement screen — Save Now offers and Text a Reminder picker"
      />
    </div>
  );
}

function TicketScreen() {
  return (
    <div className="mmx-state mmx-state-ticket">
      <div
        className="mmx-ticket-hero"
        style={{ backgroundImage: `url(${POSTERS[2]})` }}
      >
        <div className="mmx-ticket-scrim" />
        <div className="mmx-ticket-hero-meta">
          <div className="mmx-ticket-genre">ACTION · CRIME</div>
          <div className="mmx-ticket-title">The Batman<br/>Part II</div>
        </div>
      </div>
      <div className="mmx-ticket-body">
        <div className="mmx-ticket-row">
          <div className="mmx-ticket-row-k">Opens</div>
          <div className="mmx-ticket-row-v">Fri · Dec 19</div>
        </div>
        <div className="mmx-ticket-row">
          <div className="mmx-ticket-row-k">Your theatre</div>
          <div className="mmx-ticket-row-v">Cinemark West Plano · XD</div>
        </div>
        <div className="mmx-ticket-row mmx-ticket-row-price">
          <div className="mmx-ticket-row-k">Insider price</div>
          <div className="mmx-ticket-row-v">
            <span className="mmx-ticket-strike">$18.50</span>
            <span className="mmx-ticket-final">$14.00</span>
          </div>
        </div>
        <button className="mmx-ticket-cta" type="button">
          <span>Buy Now &amp; Save</span>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
        </button>
        <div className="mmx-ticket-secure">
          Secure checkout · cinemark.com
        </div>
      </div>
    </div>
  );
}

function AttributionScreen() {
  const bars = [62, 78, 54, 88, 41, 72, 95];
  return (
    <div className="mmx-state mmx-state-attr">
      <div className="mmx-attr-head">
        <div className="mmx-attr-label">Attribution — West Plano · XD</div>
        <div className="mmx-attr-title">The Batman Part II</div>
      </div>
      <div className="mmx-attr-chart">
        {bars.map((h, i) => (
          <div key={i} className="mmx-attr-bar-wrap">
            <div className="mmx-attr-bar" style={{ '--h': `${h}%` }} />
          </div>
        ))}
      </div>
      <div className="mmx-attr-ticker">
        <div className="mmx-attr-ticker-row">
          <span className="mmx-tick-dot" />
          <span className="mmx-tick-time">8:50</span>
          <span className="mmx-tick-text">Trailer view · Seat E14</span>
        </div>
        <div className="mmx-attr-ticker-row">
          <span className="mmx-tick-dot" />
          <span className="mmx-tick-time">11:06</span>
          <span className="mmx-tick-text">Trailer replay · The Batman</span>
        </div>
        <div className="mmx-attr-ticker-row">
          <span className="mmx-tick-dot is-gold" />
          <span className="mmx-tick-time">11:08</span>
          <span className="mmx-tick-text">Ticket booked · Fri 19 · $14.00</span>
        </div>
      </div>
      <div className="mmx-attr-stats">
        <div className="mmx-attr-stat">
          <div className="mmx-attr-stat-v">34%</div>
          <div className="mmx-attr-stat-k">tap-through</div>
        </div>
        <div className="mmx-attr-stat">
          <div className="mmx-attr-stat-v">$4,280</div>
          <div className="mmx-attr-stat-k">attributed · tonight</div>
        </div>
      </div>
    </div>
  );
}

const PHONE_SCREENS = {
  sms: SmsScreen,
  carousel: CarouselScreen,
  ticket: TicketScreen,
  attribution: AttributionScreen,
};

/* ---- The scene ------------------------------------------------------- */

function HowItWorksScene() {
  const sectionRef = useRef(null);
  const progress = useScenePhase(sectionRef);
  const activeIdx = clamp(Math.floor(progress), 0, ACTS.length - 1);
  const local = progress - activeIdx; // 0..1 within the current act

  // Each act crossfades its copy + phone state.
  // Dwell profile per act (measured as d = progress - i):
  //   d < -0.15 or d > 1.0 → hidden
  //   -0.15 to 0 → fade in
  //   0 to 0.85 → held at full
  //   0.85 to 1.0 → fade out (so the next act can enter at its d=-0.15)
  const actOpacities = useMemo(() => ACTS.map((_, i) => {
    const d = progress - i;
    if (d <= -0.15 || d >= 1.0) return 0;
    if (d < 0) return smoothstep((d + 0.15) / 0.15);
    if (d < 0.85) return 1;
    return smoothstep((1.0 - d) / 0.15);
  }), [progress]);

  const phoneIdx = activeIdx;

  return (
    <section className="mmx-how" id="how" aria-labelledby="how-heading" ref={sectionRef}>
      {/* Sticky stage */}
      <div className="mmx-how-stage">
        {/* Theater atmosphere */}
        <div className="mmx-theater" aria-hidden>
          <div className="mmx-curtain mmx-curtain-l" />
          <div className="mmx-curtain mmx-curtain-r" />
          <div className="mmx-screen-glow" />
          <div className="mmx-projector-cone" style={{ '--cone-op': 0.35 + 0.15 * Math.sin(progress * 1.2) }} />
          <div className="mmx-grain" />
        </div>

        {/* Section header — compact, stays visible */}
        <div className="mmx-how-header">
          <span className="mmx-eyebrow">How it works</span>
          <h2 id="how-heading" className="mmx-how-headline">
            From the credits to the next ticket. <span className="mmx-gold-phrase">Four simple steps.</span>
          </h2>
        </div>

        {/* Stage content — 2-column on desktop, stacked on mobile */}
        <div className="mmx-stage-grid">
          {/* Left: acts copy */}
          <div className="mmx-acts">
            <div className="mmx-acts-rail" aria-hidden>
              {ACTS.map((_, i) => (
                <span
                  key={i}
                  className={`mmx-acts-tick${i === activeIdx ? ' is-active' : ''}${i < activeIdx ? ' is-past' : ''}`}
                />
              ))}
            </div>
            <div className="mmx-acts-stack">
              {ACTS.map((act, i) => {
                const ActScreen = PHONE_SCREENS[act.phone];
                return (
                <div
                  key={act.n}
                  className="mmx-act"
                  style={{ opacity: actOpacities[i], pointerEvents: actOpacities[i] > 0.5 ? 'auto' : 'none' }}
                  aria-hidden={actOpacities[i] < 0.5}
                >
                  <div className="mmx-act-head">
                    <span className="mmx-act-n">{act.n}</span>
                    <span className="mmx-act-eyebrow">{act.eyebrow}</span>
                  </div>
                  <h3 className="mmx-act-title">{act.title}</h3>
                  <p className="mmx-act-body">{act.body}</p>
                  {/* Act #4 (THE PROOF) gets a Book-a-demo CTA right
                      under its copy on both desktop and mobile. */}
                  {act.phone === 'attribution' ? (
                    <div className="mmx-act-cta">
                      <button
                        type="button"
                        className="mm-btn mm-btn-primary mm-btn-lg"
                        onClick={() => { window.location.href = 'contact.html'; }}
                      >
                        Book a demo for your theater
                        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  ) : null}
                  {/* Mobile-only per-act phone preview. Act #4 gets a
                      naked frame so the chart renders without bezel. */}
                  <div className="mmx-act-phone-mobile" aria-hidden="true">
                    <PhoneFrame naked={act.phone === 'attribution'}>
                      <ActScreen />
                    </PhoneFrame>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Right: phone with crossfading states */}
          <div className="mmx-stage-phone">
            <PhoneFrame naked={ACTS[phoneIdx]?.phone === 'attribution'}>
              {ACTS.map((act, i) => {
                const Screen = PHONE_SCREENS[act.phone];
                return (
                  <div
                    key={act.phone}
                    className="mmx-screen-layer"
                    style={{
                      opacity: i === phoneIdx ? 1 : 0,
                      transform: i === phoneIdx ? 'scale(1)' : `scale(${i < phoneIdx ? 1.04 : 0.96})`,
                    }}
                    aria-hidden={i !== phoneIdx}
                  >
                    <Screen />
                  </div>
                );
              })}
            </PhoneFrame>
          </div>
        </div>

        {/* Progress indicator — at the bottom */}
        <div className="mmx-how-progress" aria-hidden>
          <div className="mmx-how-progress-track">
            <div
              className="mmx-how-progress-fill"
              style={{ width: `${clamp(progress / ACTS.length, 0, 1) * 100}%` }}
            />
          </div>
          <div className="mmx-how-progress-labels">
            {ACTS.map((a, i) => (
              <span key={a.n} className={`mmx-how-progress-label${i === activeIdx ? ' is-active' : ''}`}>
                {a.n} · {a.eyebrow.replace('THE ', '')}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

window.HowItWorksScene = HowItWorksScene;
